import React, { useCallback, useMemo, useState } from 'react';
import { Modal } from '@components/molecules/Modal';
import { Button } from '@components/atoms/Button';
import { ToggleSwitch } from '@components/atoms/ToggleSwitch';
import { Tab } from '@components/atoms/Tab';
import { Loading } from '@components/atoms/Loading';
import { FileDown, FileText } from 'lucide-react';
import { InvoiceManagerSlider } from '@components/organisms/InvoiceManagerSlider';
import { useFeatureAccess } from '@utils/featureFlag';
import {
  generateBudgetPdf,
  generateAllBudgetPdf,
  generateDelegatedTasksPdf,
  generateExpensesPdf,
  AllBudgetPdfRow,
} from '@utils/budgetPdf';
import { useGetProjectBudgetSummary } from '@hooks/api/lineItem/useGetProjectBudgetSummary';
import { BudgetSummaryHeader } from './components/BudgetSummaryHeader';
import { MaterialsEquipmentTab } from './components/MaterialsEquipmentTab';
import { DelegatedTasksTab } from './components/DelegatedTasksTab';
import { LineItemsTab } from './components/LineItemsTab';
import { AllItemsTab } from './components/AllItemsTab';
import { ByTaskTab } from './components/ByTaskTab';

type Project = {
  name?: string;
  budget?: number | null;
  originalProjectId?: string;
  originalProject?: {
    projectId: string;
    name: string;
    organization: { name?: string };
  };
  client?: { name?: string; email?: string };
  projectManager?: {
    user?: { firstName?: string; lastName?: string };
  };
  superintendents?: Array<{
    user?: { firstName?: string; lastName?: string };
  }>;
  state?: string;
};

interface BudgetTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItem?: Project | null;
  isDelegated: boolean;
  projectId: string;
}

export const BudgetTableModal: React.FC<BudgetTableModalProps> = ({
  isOpen,
  onClose,
  selectedItem,
  isDelegated = false,
  projectId,
}) => {
  const [filterLevel1, setFilterLevel1] = useState<'genre' | 'task'>('genre');
  const [activeGenre, setActiveGenre] = useState<
    'all' | 'materials' | 'delegated' | 'expenses'
  >('all');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [includeCosts, setIncludeCosts] = useState(true);
  const [isInvoiceSliderOpen, setIsInvoiceSliderOpen] = useState(false);
  const { canAccess } = useFeatureAccess();
  const canInvoice = canAccess('INVOICES');

  const { data: summary, isLoading } = useGetProjectBudgetSummary(
    isOpen ? projectId : null,
  );

  const inventory = useMemo(() => {
    if (!summary) return [];
    return [...(summary.materials || []), ...(summary.equipments || [])];
  }, [summary]);

  const materialCount = useMemo(
    () => (summary?.materials || []).filter((m: any) => {
      const hasCost = m.totalCost !== null && m.totalCost !== undefined && !isNaN(m.totalCost);
      const hasPrice = m.totalPrice !== null && m.totalPrice !== undefined && !isNaN(m.totalPrice);
      return hasCost || hasPrice;
    }).length,
    [summary],
  );

  const equipmentCount = useMemo(
    () => (summary?.equipments || []).filter((e: any) => {
      const hasCost = e.totalCost !== null && e.totalCost !== undefined && !isNaN(e.totalCost);
      const hasPrice = e.totalPrice !== null && e.totalPrice !== undefined && !isNaN(e.totalPrice);
      return hasCost || hasPrice;
    }).length,
    [summary],
  );

  const delegatedCount = summary?.delegatedTaskCosts?.length || 0;
  const expenseCount = summary?.lineItems?.length || 0;

  const showDelegatedTab = !isDelegated && delegatedCount > 0;

  const byTaskCount = useMemo(() => {
    const taskIds = new Set<string>();
    inventory.forEach((item: any) => {
      const hasCost = item.totalCost !== null && item.totalCost !== undefined && !isNaN(item.totalCost);
      const hasPrice = item.totalPrice !== null && item.totalPrice !== undefined && !isNaN(item.totalPrice);
      if ((hasCost || hasPrice) && item.taskId) {
        taskIds.add(item.taskId);
      }
    });
    return taskIds.size;
  }, [inventory]);

  const level1Tabs = useMemo(() => {
    const t: Array<{ id: string; name: string; count?: number }> = [
      { id: 'genre', name: 'By Genre' },
    ];
    if (byTaskCount > 0) {
      t.push({ id: 'task', name: 'By Task', count: byTaskCount });
    }
    return t;
  }, [byTaskCount]);

  const genreTabs = useMemo(() => {
    const t: Array<{ id: string; name: string; count?: number }> = [
      { id: 'all', name: 'All' },
      {
        id: 'materials',
        name: 'Mat & Equip',
        count: materialCount + equipmentCount,
      },
    ];
    if (showDelegatedTab) {
      t.push({ id: 'delegated', name: 'Delegated', count: delegatedCount });
    }
    t.push({ id: 'expenses', name: 'Line Items', count: expenseCount });
    return t;
  }, [
    materialCount,
    equipmentCount,
    delegatedCount,
    expenseCount,
    showDelegatedTab,
  ]);

  const handleExportPdf = useCallback(() => {
    if (!selectedItem || !inventory.length) return;
    generateBudgetPdf({
      inventory,
      project: selectedItem,
      includeCosts,
      isDelegated,
      filterLabel: 'All Items',
    });
    setIsExportModalOpen(false);
  }, [selectedItem, inventory, includeCosts, isDelegated]);

  const handleExportDelegated = useCallback(() => {
    if (!selectedItem || !summary) return;
    generateDelegatedTasksPdf({
      items: summary.delegatedTaskCosts || [],
      projectName: selectedItem.name || 'Project',
    });
  }, [selectedItem, summary]);

  const handleExportExpenses = useCallback(() => {
    if (!selectedItem || !summary) return;
    generateExpensesPdf({
      items: summary.lineItems || [],
      projectName: selectedItem.name || 'Project',
    });
  }, [selectedItem, summary]);

  const handleExportAll = useCallback(() => {
    if (!selectedItem || !summary) return;

    const rows: AllBudgetPdfRow[] = [];

    (summary.materials || []).forEach((m: any) => {
      if (m.totalCost || m.totalPrice) {
        rows.push({ category: 'Material', name: m.name || '-', totalCost: m.totalCost || 0 });
      }
    });
    (summary.equipments || []).forEach((e: any) => {
      if (e.totalCost || e.totalPrice) {
        rows.push({ category: 'Equipment', name: e.name || '-', totalCost: e.totalCost || 0 });
      }
    });
    (summary.delegatedTaskCosts || []).forEach((d) => {
      rows.push({ category: 'Delegated', name: `${d.taskName} (${d.organizationName})`, totalCost: d.budget || 0 });
    });
    (summary.lineItems || []).forEach((e) => {
      rows.push({ category: 'Line Item', name: e.description, totalCost: e.cost || 0 });
    });

    generateAllBudgetPdf({
      rows,
      project: selectedItem,
      totals: {
        materials: summary.totalMaterialsCost || 0,
        equipment: summary.totalEquipmentsCost || 0,
        delegated: summary.totalDelegatedCost || 0,
        lineItems: summary.totalLineItemsCost || 0,
        total: summary.totalAmount || 0,
      },
    });
  }, [selectedItem, summary]);

  if (!isOpen) return null;

  const pmUser = selectedItem?.projectManager?.user;
  const pmName = pmUser ? `${pmUser.firstName} ${pmUser.lastName}` : undefined;
  const suptUser = selectedItem?.superintendents?.[0]?.user;
  const superintendentName = suptUser
    ? `${suptUser.firstName} ${suptUser.lastName}`
    : undefined;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={selectedItem?.name || 'Project Budget'}
      size="full"
      closeOnOverlayClick
      confirmText="Close"
      handleConfirm={onClose}>
      <div className="p-2">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Loading size="xl" />
          </div>
        ) : (
          <>
            {/* Summary Header — always visible */}
            <BudgetSummaryHeader
              totalMaterialsCost={summary?.totalMaterialsCost || 0}
              totalEquipmentsCost={summary?.totalEquipmentsCost || 0}
              totalDelegatedCost={summary?.totalDelegatedCost || 0}
              totalLineItemsCost={summary?.totalLineItemsCost || 0}
              totalAmount={summary?.totalAmount || 0}
              projectBudget={selectedItem?.budget || null}
              isDelegated={isDelegated}
              materialCount={materialCount}
              equipmentCount={equipmentCount}
              delegatedCount={delegatedCount}
              expenseCount={expenseCount}
              projectState={selectedItem?.state}
              originOrgName={
                selectedItem?.originalProject?.organization?.name
              }
              clientName={selectedItem?.client?.name}
              pmName={pmName}
              superintendentName={superintendentName}
            />

            {/* Two-level filter navigation + Export button */}
            <div className="flex flex-col gap-2 mb-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <Tab
                    tabs={level1Tabs}
                    activeTab={filterLevel1}
                    onTabChange={(id) =>
                      setFilterLevel1(id as 'genre' | 'task')
                    }
                    variant="underline"
                    size="sm"
                  />
                </div>
                <div className="flex-shrink-0 flex items-center gap-2">
                  {!isDelegated && canInvoice && (
                    <Button
                      variant="primary"
                      leftIcon={<FileText size={14} />}
                      onClick={() => setIsInvoiceSliderOpen(true)}>
                      Invoices
                    </Button>
                  )}
                  {filterLevel1 === 'genre' && (
                    <Button
                      variant="tertiary"
                      leftIcon={<FileDown size={14} />}
                      onClick={() => {
                        if (activeGenre === 'all') handleExportAll();
                        else if (activeGenre === 'materials') {
                          setIncludeCosts(true);
                          setIsExportModalOpen(true);
                        } else if (activeGenre === 'delegated')
                          handleExportDelegated();
                        else if (activeGenre === 'expenses')
                          handleExportExpenses();
                      }}>
                      Export PDF
                    </Button>
                  )}
                </div>
              </div>

              {filterLevel1 === 'genre' && (
                <Tab
                  tabs={genreTabs}
                  activeTab={activeGenre}
                  onTabChange={(id) =>
                    setActiveGenre(
                      id as 'all' | 'materials' | 'delegated' | 'expenses',
                    )
                  }
                  variant="pills"
                  size="sm"
                />
              )}
            </div>

            {/* Tab content */}
            {filterLevel1 === 'task' ? (
              <ByTaskTab inventory={inventory} isDelegated={isDelegated} />
            ) : (
              <>
                {activeGenre === 'all' && (
                  <AllItemsTab
                    inventory={inventory}
                    delegatedTaskCosts={summary?.delegatedTaskCosts || []}
                    lineItems={summary?.lineItems || []}
                  />
                )}

                {activeGenre === 'materials' && (
                  <MaterialsEquipmentTab
                    inventory={inventory}
                    isDelegated={isDelegated}
                  />
                )}

                {activeGenre === 'delegated' && (
                  <DelegatedTasksTab
                    delegatedTaskCosts={summary?.delegatedTaskCosts || []}
                  />
                )}

                {activeGenre === 'expenses' && (
                  <LineItemsTab
                    lineItems={summary?.lineItems || []}
                    isDelegated={isDelegated}
                    projectId={projectId}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Invoice manager slider — Create / browse drafts, sent, paid */}
      {canInvoice && (
        <InvoiceManagerSlider
          isOpen={isInvoiceSliderOpen}
          projectId={projectId}
          projectName={selectedItem?.name}
          defaultRecipientEmail={selectedItem?.client?.email}
          onClose={() => setIsInvoiceSliderOpen(false)}
        />
      )}

      {/* Export PDF Config Modal */}
      <Modal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        title="Export PDF Configuration"
        description="Choose which information to include in the exported PDF."
        size="small"
        handleConfirm={handleExportPdf}
        confirmText="Export PDF"
        cancelText="Cancel">
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-900">
                Include Costs
              </p>
              <p className="text-xs text-gray-500">
                Show unit cost, cost/hour, and total cost columns
              </p>
            </div>
            <ToggleSwitch
              leftLabel=""
              rightLabel=""
              checked={includeCosts}
              onChange={setIncludeCosts}
            />
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

export default BudgetTableModal;
